/**
 * María — Asistente de TAH v4
 * - Chat en burbujas arrastrable, persistente durante el día (no se reinicia al cerrar)
 * - Renderiza markdown básico
 * - Highlight + navegación automática: si el botón vive en otra página, María te lleva allí
 *   y luego lo resalta — Opción A (decisión 30/06/2026)
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { trackEvento } from "@/hooks/useTrackEvento";
import { api } from "@/lib/api";
import { KEY_DIA, KEY_ONBOARDING, KEY_CONVERSACION } from "@/lib/mariaStorage";

const MARIA_IMG = "/maria.png";
const AZUL      = "#2563EB";
const CONTACT_EMAIL = "info@tualiadohostelero.com";

// Tope de mensajes que se reenvían al backend en cada turno — la conversación
// completa del día se guarda y se muestra igual, pero sin esto el payload crece
// sin límite conforme pasa el día (más lento y más caro cuanto más se habla con María).
const MAX_MENSAJES_ENVIADOS = 20;

// ── Helpers de fecha y estado ──────────────────────────────────────────────────
const hoyStr = () => new Date().toISOString().split("T")[0];
const saludoDelDia = (nombre, t) => {
  const h = new Date().getHours();
  const periodo = h < 14 ? "morning" : h < 21 ? "afternoon" : "evening";
  const m = t(`maria.greeting.${periodo}`);
  return `${m}${nombre ? `, ${nombre}` : ""} ${t("maria.greeting.question")}`;
};
const esNuevoDia = () => localStorage.getItem(KEY_DIA) !== hoyStr();
const marcarDia  = () => localStorage.setItem(KEY_DIA, hoyStr());
const onboardingHecho  = () => localStorage.getItem(KEY_ONBOARDING) === "1";
const marcarOnboarding = () => localStorage.setItem(KEY_ONBOARDING, "1");

// ── Persistencia de conversación — dura todo el día, se reinicia al día siguiente ─
const cargarConversacion = () => {
  try {
    const raw = localStorage.getItem(KEY_CONVERSACION);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.fecha !== hoyStr()) return null; // de otro día, no vale
    return data.mensajes;
  } catch { return null; }
};
const guardarConversacion = (mensajes) => {
  try {
    localStorage.setItem(KEY_CONVERSACION, JSON.stringify({ fecha: hoyStr(), mensajes }));
  } catch {}
};

// ── Renderizar markdown básico ────────────────────────────────────────────────
const EMAIL_RE = /([\w.+-]+@[\w-]+\.[\w.-]+)/g;

/** Trocea una línea de texto detectando direcciones de correo y devolviéndolas como mailto: */
function renderConCorreos(texto, keyPrefix) {
  const trozos = texto.split(EMAIL_RE);
  return trozos.map((trozo, i) => {
    if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(trozo)) {
      return (
        <a
          key={`${keyPrefix}-${i}`}
          href={`mailto:${trozo}`}
          className="font-semibold underline"
          style={{ color: AZUL }}
        >
          {trozo}
        </a>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{trozo}</React.Fragment>;
  });
}

function MdText({ text }) {
  const partes = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <span>
      {partes.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
          return <strong key={i}>{renderConCorreos(p.slice(2, -2), `b${i}`)}</strong>;
        }
        return p.split("\n").map((linea, j) => (
          <React.Fragment key={`${i}-${j}`}>
            {j > 0 && <br />}
            {renderConCorreos(linea, `${i}-${j}`)}
          </React.Fragment>
        ));
      })}
    </span>
  );
}

// ── Sistema de highlights ─────────────────────────────────────────────────────
const HIGHLIGHT_CSS = `
  @keyframes maria-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(37,99,235,0.6), 0 0 0 8px rgba(37,99,235,0.3), 0 0 16px 4px rgba(37,99,235,0.4); transform: scale(1); }
    50%       { box-shadow: 0 0 0 10px rgba(37,99,235,0.4), 0 0 0 18px rgba(37,99,235,0.15), 0 0 24px 8px rgba(37,99,235,0.5); transform: scale(1.06); }
  }
  .maria-highlight {
    animation: maria-pulse 1.3s ease-in-out infinite !important;
    outline: 3px solid #2563EB !important;
    outline-offset: 3px !important;
    border-radius: 8px !important;
    position: relative !important;
    z-index: 30 !important;
  }
`;
let styleInjected = false;
const injectStyle = () => {
  if (styleInjected) return;
  const el = document.createElement("style");
  el.textContent = HIGHLIGHT_CSS;
  document.head.appendChild(el);
  styleInjected = true;
};

// Extrae [HIGHLIGHT:id] y [NAVEGA:/ruta] del texto de María
const extraerEtiquetas = (texto) => {
  const reHighlight = /\[HIGHLIGHT:([^\]]+)\]/g;
  const reNavega    = /\[NAVEGA:([^\]]+)\]/g;
  const ids = [];
  const rutas = [];
  let m;
  while ((m = reHighlight.exec(texto)) !== null) ids.push(m[1]);
  while ((m = reNavega.exec(texto)) !== null) rutas.push(m[1]);
  const textoLimpio = texto.replace(reHighlight, "").replace(reNavega, "").trim();
  return { textoLimpio, ids, ruta: rutas[0] || null };
};

const intentarResaltarUno = (id) => {
  const el = document.querySelector(`[data-testid="${id}"]`);
  if (el) {
    el.classList.add("maria-highlight");
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.addEventListener("click", () => el.classList.remove("maria-highlight"), { once: true });
    return true;
  }
  return false;
};

const aplicarHighlights = (ids) => {
  injectStyle();
  document.querySelectorAll(".maria-highlight").forEach((el) => el.classList.remove("maria-highlight"));
  ids.forEach((id) => {
    // Intento inmediato; si la página acaba de cambiar (navegación), el elemento
    // puede tardar un instante en montarse — seguimos observando el DOM unos segundos
    if (!intentarResaltarUno(id)) {
      const observer = new MutationObserver(() => {
        if (intentarResaltarUno(id)) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 8000);
    }
  });
};

// ── Panel de chat ─────────────────────────────────────────────────────────────
function MariaPanel({ nombre, esNuevoUsuario, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const mensajeInicial = esNuevoUsuario
    ? { role: "assistant", content: t("maria.onboarding") }
    : { role: "assistant", content: saludoDelDia(nombre, t) };

  const [mensajes, setMensajes] = useState(() => cargarConversacion() || [mensajeInicial]);
  const [input, setInput]       = useState("");
  const [cargando, setCargando] = useState(false);
  const [fallback, setFallback] = useState(false);
  const bottomRef  = useRef(null);
  const panelRef   = useRef(null);
  const primerRender = useRef(true);

  // Guardar conversación en cada cambio
  useEffect(() => { guardarConversacion(mensajes); }, [mensajes]);

  // ── Drag ──────────────────────────────────────────────────────────────────
  const [pos, setPos]   = useState(null);
  const dragging        = useRef(false);
  const dragOffset      = useRef({ x: 0, y: 0 });

  // Mantiene la cabecera siempre dentro de la pantalla (imposible perder el panel).
  const clampPos = (x, y) => {
    const el = panelRef.current;
    const w = el ? el.offsetWidth : 0;
    const headerVisible = 48; // px de la cabecera que siempre quedan agarrables
    const maxX = Math.max(0, window.innerWidth - w);
    const maxY = Math.max(0, window.innerHeight - headerVisible);
    return {
      x: Math.min(Math.max(0, x), maxX),
      y: Math.min(Math.max(0, y), maxY),
    };
  };

  const startDrag = (cx, cy) => {
    if (!panelRef.current) return;
    const r = panelRef.current.getBoundingClientRect();
    dragging.current = true;
    dragOffset.current = { x: cx - r.left, y: cy - r.top };
    setPos(clampPos(r.left, r.top));
  };

  // Doble clic en la cabecera → vuelve a su posición original (esquina inferior derecha).
  const resetPos = () => setPos(null);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      if (e.cancelable) e.preventDefault();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      setPos(clampPos(cx - dragOffset.current.x, cy - dragOffset.current.y));
    };
    const onUp = () => { dragging.current = false; };
    // Si la ventana cambia de tamaño y el panel quedó movido, re-encájalo dentro.
    const onResize = () => setPos((p) => (p ? clampPos(p.x, p.y) : p));
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const panelStyle = pos
    ? { position: "fixed", left: pos.x, top: pos.y, bottom: "auto", right: "auto" }
    : {};

  // Scroll al fondo — instantáneo y exacto, sin animación que deje la última línea a medias
  useEffect(() => {
    if (primerRender.current) { primerRender.current = false; }
    bottomRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [mensajes, cargando]);

  // ── Detección local de "no quiero onboarding" ──────────────────────────────
  const procesarRespuestaOnboarding = (texto) => {
    const low = texto.toLowerCase();
    if (low.includes("no") || low.includes("ya sé") || low.includes("ya se") || low.includes("solo")) {
      marcarOnboarding();
      return t("maria.noOnboarding");
    }
    return null;
  };

  // ── Enviar mensaje ────────────────────────────────────────────────────────
  const enviar = async () => {
    const texto = input.trim();
    if (!texto || cargando) return;
    setInput("");

    const nuevos = [...mensajes, { role: "user", content: texto }];
    setMensajes(nuevos);
    setCargando(true);

    if (esNuevoUsuario && !onboardingHecho()) {
      const respLocal = procesarRespuestaOnboarding(texto);
      if (respLocal) {
        setTimeout(() => {
          setMensajes([...nuevos, { role: "assistant", content: respLocal }]);
          setCargando(false);
        }, 600);
        return;
      }
    }

    try {
      const { data } = await api.post("/maria/chat", {
        messages: nuevos.slice(-MAX_MENSAJES_ENVIADOS).map((m) => ({ role: m.role, content: m.content })),
        lang: i18n.language?.startsWith("en") ? "en" : "es",
      });
      const raw = data?.content || t("maria.notUnderstood");
      const { textoLimpio, ids, ruta } = extraerEtiquetas(raw);

      // Si María indica una ruta y no estamos ya en ella, navegamos primero (Opción A)
      if (ruta && location.pathname !== ruta) {
        navigate(ruta);
      }
      if (ids.length > 0) {
        // Pequeño margen para que la página destino monte sus componentes
        setTimeout(() => aplicarHighlights(ids), ruta ? 250 : 0);
      }
      if (textoLimpio.includes(CONTACT_EMAIL)) setFallback(true);
      setMensajes([...nuevos, { role: "assistant", content: textoLimpio }]);
    } catch {
      setMensajes([...nuevos, { role: "assistant", content: t("maria.connectionError") }]);
      setFallback(true);
    } finally {
      setCargando(false);
    }
  };

  return (
    <div
      ref={panelRef}
      className="fixed bottom-40 right-3 left-3 z-50 flex max-h-[65vh] flex-col rounded-2xl border border-brand-edge bg-card shadow-2xl sm:left-auto sm:right-6 sm:w-96 sm:max-h-[80vh]"
      style={panelStyle}
      data-testid="maria-chat"
    >
      {/* Cabecera arrastrable */}
      <div
        className="flex shrink-0 items-center gap-3 rounded-t-2xl px-4 py-3 cursor-grab active:cursor-grabbing select-none"
        style={{ backgroundColor: AZUL, touchAction: "none" }}
        onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
        onTouchStart={(e) => { e.preventDefault(); startDrag(e.touches[0].clientX, e.touches[0].clientY); }}
        onDoubleClick={resetPos}
        title={t("maria.resetPositionHint")}
      >
        <img src={MARIA_IMG} alt="María" width="150" height="150" loading="lazy" className="h-9 w-9 rounded-full object-cover ring-2 ring-white/20 pointer-events-none" />
        <div className="flex-1 pointer-events-none">
          <p className="text-sm font-semibold text-white">{t("maria.name")}</p>
          <p className="text-xs text-white/70 hidden sm:block">{t("maria.subtitle")}</p>
        </div>
        <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={onClose} className="text-white/70 hover:text-white transition-colors">
          <X size={18} />
        </button>
      </div>

      {/* Burbujas */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
        {mensajes.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
            {m.role === "assistant" && (
              <img src={MARIA_IMG} alt="" width="150" height="150" loading="lazy" className="h-7 w-7 shrink-0 rounded-full object-cover self-end" />
            )}
            <div
              className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                m.role === "user" ? "rounded-tr-sm text-white" : "rounded-tl-sm bg-brand-cream text-brand-ink"
              }`}
              style={m.role === "user" ? { backgroundColor: AZUL } : {}}
            >
              <MdText text={m.content} />
            </div>
          </div>
        ))}
        {cargando && (
          <div className="flex gap-2">
            <img src={MARIA_IMG} alt="" width="150" height="150" loading="lazy" className="h-7 w-7 shrink-0 rounded-full object-cover self-end" />
            <div className="rounded-2xl rounded-tl-sm bg-brand-cream px-4 py-2 text-sm text-brand-stone">
              <span className="animate-pulse">···</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Fallback email */}
      {fallback && (
        <div className="mx-4 mb-2 shrink-0 rounded-lg border border-brand-edge bg-brand-cream/60 px-3 py-2 text-xs text-brand-stone">
          {t("maria.needHelp")}{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Necesito ayuda con TAH`}
            className="inline-block py-1 font-semibold underline"
            style={{ color: AZUL }}
          >
            {t("maria.clickHere")}
          </a>{" "}
          {t("maria.helpSuffix")}
        </div>
      )}

      {/* Input */}
      <div className="flex shrink-0 items-center gap-2 border-t border-brand-edge px-3 py-3">
        <input
          className="flex-1 rounded-full border border-brand-edge bg-brand-cream px-4 py-2 text-sm outline-none focus:border-brand-stone"
          placeholder={t("maria.placeholder")}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.isComposing) {
              e.preventDefault();
              enviar();
            }
          }}
        />
        <button
          type="button"
          onClick={enviar}
          disabled={!input.trim() || cargando}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white disabled:opacity-40 transition-opacity"
          style={{ backgroundColor: AZUL }}
        >
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}

// ── Botón flotante circular ───────────────────────────────────────────────────
export function MariaBoton() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nombre = user?.name?.split(" ")[0] || "";
  const [abierto, setAbierto]        = useState(false);
  const [esNuevoUsuario, setEsNuevo] = useState(false);

  useEffect(() => {
    const nuevo = !onboardingHecho();
    if (esNuevoDia()) {
      marcarDia();
      setEsNuevo(nuevo);
      if (nuevo) marcarOnboarding(); // la oferta de configurar solo se hace una vez en la vida
      const t = setTimeout(() => setAbierto(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <>
      {abierto && (
        <MariaPanel
          nombre={nombre}
          esNuevoUsuario={esNuevoUsuario}
          onClose={() => setAbierto(false)}
        />
      )}
      <button
        onClick={() => setAbierto((v) => {
          const next = !v;
          if (next) trackEvento("abrir_chat_maria", "maria-chat");
          return next;
        })}
        title={t("maria.tooltip")}
        className="fixed bottom-20 right-6 z-40 h-14 w-14 overflow-hidden rounded-full border-2 border-white shadow-lg hover:scale-105 transition-transform"
        data-testid="maria-boton"
      >
        <img src={MARIA_IMG} alt={t("maria.tooltip")} width="150" height="150" loading="lazy" className="h-full w-full object-cover" />
      </button>
    </>
  );
}
